// ---------------------------------------------------------------------------
// DSS-26: Style State Injector — collects and injects generated CSS
// ---------------------------------------------------------------------------
// Renders a single <style> element (via React portal into <head>) containing
// all :hover/:focus/:active rules for every Puck component on the page.
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { computeDssStyles, type DssStyles } from "@ease/page-builder";
import {
  buildScopedClassName,
  extractStateOverrides,
  extractTransitionConfig,
  generateStateCSS,
} from "./style-state-system";

interface PuckContentItem {
  type?: string;
  props?: Record<string, unknown>;
}

interface StyleStateInjectorProps {
  /** The Puck data.content array — each item may have _hoverStyle etc. */
  content: PuckContentItem[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Collects all state CSS from Puck component data and injects a single
 * <style> tag into <head>. Re-renders when content changes.
 */
export default function StyleStateInjector({
  content,
}: StyleStateInjectorProps) {
  const styleId = useId();
  const [cssText, setCssText] = useState("");

  const buildCSS = useCallback(() => {
    const rules: string[] = [];

    content.forEach((item, index) => {
      if (!item.props) return;

      const componentId = typeof item.props.id === "string" ? item.props.id : `block-${index + 1}`;
      const scopedClass = buildScopedClassName(componentId);

      if (isRecord(item.props._dssStyles)) {
        const computed = computeDssStyles(item.props._dssStyles as DssStyles, { scopedClass });
        if (computed.cssRules) {
          const safeType = (item.type ?? "unknown")
            .replace(/\*\//g, "")
            .replace(/\/\*/g, "")
            .replace(/\\/g, "")
            .replace(/[{}]/g, "")
            .slice(0, 64);
          rules.push(`/* ${safeType} [${index}] */`);
          rules.push(computed.cssRules);
        }
        return;
      }

      const overrides = extractStateOverrides(item.props);
      const transitions = extractTransitionConfig(item.props);
      const css = generateStateCSS(scopedClass, overrides, transitions);
      if (css) {
        const safeType = (item.type ?? "unknown")
          .replace(/\*\//g, "")
          .replace(/\/\*/g, "")
          .replace(/\\/g, "")
          .replace(/[{}]/g, "")
          .slice(0, 64);
        rules.push(`/* ${safeType} [${index}] */`);
        rules.push(css);
      }
    });

    return rules.join("\n");
  }, [content]);

  useEffect(() => {
    setCssText(buildCSS());
  }, [buildCSS]);

  if (!cssText) return null;

  // Portal into <head> — no dangerouslySetInnerHTML
  return createPortal(
    <StyleTag id={`puck-states-${styleId}`} css={cssText} />,
    document.head,
  );
}

// ---------------------------------------------------------------------------
// StyleTag — renders CSS text as a proper text node (no innerHTML)
// ---------------------------------------------------------------------------

interface StyleTagProps {
  id: string;
  css: string;
}

function StyleTag({ id, css }: StyleTagProps) {
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    if (styleRef.current) {
      styleRef.current.textContent = css;
    }
  }, [css]);

  return (
    <style
      ref={(el) => {
        if (el && el !== styleRef.current) {
          styleRef.current = el;
          el.textContent = css;
        }
      }}
      data-puck-states={id}
    />
  );
}
